import getOptions from "@app/projects/create/data/options";
import { uniqueId } from "lodash";
import { Component, Fragment, RefObject } from "react";
import { FeatureFlagContextProps, withFeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import Project from "@app/projects/project";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { UserContext } from "@app/user/UserContext";
import BulkProjectEditChange from "@app/projects/edit/bulkProjectEditChange";
import { ProjectExclusionType } from "@app/projects/enumerators";

interface EditDisplayOptionsProps {
  sidePanelId?: string;
  accuracyAndAdequacy: boolean;
  checkboxClass: string;
  disabled: boolean;
  disableUnsupportedOptions: boolean;
  onCheckBoxChange: (property: UpdateResourceKeys) => void;
  onImpactExclusionsChange: (exclusion: ProjectExclusionType) => void;
  onNetworkExclusionsChange: (exclusion: ProjectExclusionType) => void;
  project: Project;
  shared: boolean;
  hideClosestProviderReportOption: boolean;
  hideCMSApplicationCredit: boolean;
  hideCreateStandards: boolean;
  hideMotiveAppropriatenessScore: boolean;
  hideEnterpriseComplianceDashboard: boolean;
  hidePracAtLocCondifence?: boolean;
  hideProviderClaimsActivity?: boolean;
  hideStrictTimeDistance: boolean;
  ecdCheckboxRef?: RefObject<HTMLInputElement>;
  isDataShareEligible: boolean;
  isZipProject: boolean;
  zipVolumeTargetingEnabled?: boolean;

  // Be careful with the following properties:
  // They look unused, but they aren't (see: this.props[field.key])
  // Names must match those in 'ts\projects\create\data\options.ts'
  excludeDeactivatedFacilitiesImpact?: boolean;
  excludeDeactivatedFacilitiesNetwork?: boolean;
  excludeDeactivatedProvidersImpact?: boolean;
  excludeDeactivatedProvidersNetwork?: boolean;
  excludeInvalidNppesNpiFacilitiesImpact?: boolean;
  excludeInvalidNppesNpiFacilitiesNetwork?: boolean;
  excludeInvalidNppesNpiProvidersImpact?: boolean;
  excludeInvalidNppesNpiProvidersNetwork?: boolean;
  excludeMedicareOptOutProvidersImpact?: boolean;
  excludeMedicareOptOutProvidersNetwork?: boolean;
  excludeMissingNpiFacilitiesImpact?: boolean;
  excludeMissingNpiFacilitiesNetwork?: boolean;
  excludeMissingNpiProvidersImpact?: boolean;
  excludeMissingNpiProvidersNetwork?: boolean;
  excludeOIGExcludedProvidersImpact?: boolean;
  excludeOIGExcludedProvidersNetwork?: boolean;
  excludePrecludedProvidersImpact?: boolean;
  excludePrecludedProvidersNetwork?: boolean;
  useAdequacy: boolean;
  useAccuracy: boolean;
  showAffiliationsInsights: boolean;
  affiliationsDisplayOnNetworkAccordion: boolean;
}

type UpdateResourceKeys = keyof Project | keyof BulkProjectEditChange;

class WrappedEditDisplayOptions extends Component<EditDisplayOptionsProps & FeatureFlagContextProps> {
  public render() {
    return (
      <Fragment>
        <div className="tab-contents col-12">
          {!this.props.shared &&
            <>
              <UserContext.Consumer>
                {(user) => (
                  getOptions<Project>({
                    accuracyAndAdequacy: this.props.accuracyAndAdequacy,
                    affiliationsDisplayOnNetworkAccordion: this.props.affiliationsDisplayOnNetworkAccordion,
                    checkboxRefs: {
                      "includeInEnterpriseComplianceDashboard": this.props.ecdCheckboxRef,
                    },
                    disableUnsupportedOptions: this.props.disableUnsupportedOptions,
                    enableNetworkIntegrityOption: this.props.project.adequacyTemplateCollectionId != null && !this.props.useAccuracy,
                    enablePatternOfCareProvidersOption: this.props.project.hasZipSummaries && this.props.project.showNextClosestNetworkProviders,
                    enablePgrConfidenceTierOption: this.props.project.adequacyTemplateCollectionId != null,
                    enableRegulatoryExceptionReportsOption: this.props.project.hasZipSummaries && this.props.project.showNextClosestNetworkProviders,
                    hideClosestProviderReport: this.props.hideClosestProviderReportOption,
                    hideCMSApplicationCredit: this.props.hideCMSApplicationCredit,
                    hideCreateStandards: this.props.hideCreateStandards,
                    hideEnterpriseComplianceDashboard: this.props.hideEnterpriseComplianceDashboard,
                    hideMotiveAppropriatenessScore: this.props.hideMotiveAppropriatenessScore,
                    hidePciData: !this.props.useAdequacy,
                    hidePracAtLocCondifence: this.props.hidePracAtLocCondifence,
                    hideProviderClaimsActivity: this.props.hideProviderClaimsActivity,
                    hideRppoNonContractedProvidersIncludedInHsd: !this.props.enabledFeatureFlags.has(Features.RPPO_NON_PAR_PROVIDER_TO_HSD_TABLE_INCLUSION),
                    hideSnowflakeProjectRunProviderSets: !this.props.enabledFeatureFlags.has(Features.SNOWFLAKE_PROJECT_RUN_PROVIDER_SETS),
                    hideStrictTimeDistance: this.props.hideStrictTimeDistance,
                    isDataShareEligible: this.props.isDataShareEligible,
                    isZipProject: this.props.isZipProject,
                    showAffiliationsInsights: this.props.showAffiliationsInsights,
                    showNcpInStateProvidersOnly: this.props.enabledFeatureFlags.has(Features.ZIP_NEXT_CLOSEST_PROVIDERS) && this.props.project.showNextClosestNetworkProviders,
                    showNetworkIntegrityDashboardChecked: this.props.project.showNetworkIntegrityDashboard,
                    showPciDataChecked: this.props.project.showPciData,
                    showZipAnalysisNextClosestProviders: this.props.enabledFeatureFlags.has(Features.ZIP_NEXT_CLOSEST_PROVIDERS),
                    showZipGapAgeTracking: this.props.enabledFeatureFlags.has(Features.ZIP_GAP_AGE_TRACKING),
                    useAccuracy: this.props.useAccuracy,
                    userRoles: user.roles,
                    zipVolumeTargetingEnabled: this.props.zipVolumeTargetingEnabled ?? this.props.enabledFeatureFlags.has(Features.ZIP_CODE_ANALYSIS_VOLUME_TARGETING),
                  }).map((fieldset) => (
                    fieldset.fields.length > 0 && (
                      <div className="form-group row" key={fieldset.title}>
                        <label className="col-12 col-sm-3 col-lg-3 col-form-label pt-0">
                          {fieldset.title}
                        </label>
                        <div className="col-12 col-sm-9 col-lg-8">
                          {fieldset.fields.map((field) => (
                            !!field.exclusionType ? (
                              this.renderExclusionCheckbox(field.title,
                                field.exclusionType,
                                this.props.checkboxClass,
                                this.props.disabled || field.disabled,
                                !!field.impact,
                                this.props[field.key as keyof EditDisplayOptionsProps] === undefined ? undefined : !!this.props[field.key as keyof EditDisplayOptionsProps],
                                field.ref,
                              )
                            ) :
                              (
                                this.renderCheckbox(
                                  field.key,
                                  field.title,
                                  field.key as keyof Project,
                                  this.props.checkboxClass,
                                  "col-12 col-sm-9 col-lg-8",
                                  this.props.disabled || field.disabled,
                                  this.props.project[field.key as keyof Project] === undefined ? undefined : !!this.props.project[field.key as keyof Project],
                                  field.tooltip,
                                  !!field.dependency,
                                  field.ref
                                )
                              )
                          ))}
                        </div>
                      </div>
                    )
                  ))
                )}
              </UserContext.Consumer>
            </>
          }
        </div>
      </Fragment>
    );
  }
  private renderExclusionCheckbox(
    checkboxText: string,
    projectExclusionType: ProjectExclusionType,
    checkboxCssClass: string,
    disabled: boolean,
    impact: boolean,
    checkboxValue?: boolean,
    checkboxRef?: RefObject<HTMLInputElement>,
  ) {
    if (this.props.shared && !checkboxValue) {
      //only display checked checkboxes for shared users
      return null;
    }
    const inputId = uniqueId();
    return (
      <div className="col-12" key={checkboxText}>
        <QESCheckbox
          containerClassName={`${checkboxCssClass} form-check mt-0`}
          label={<span style={{ color: checkboxValue == undefined ? "red" : "inherit" }}>{checkboxText}</span>}
          id={inputId}
          className="custom-control-inline"
          checked={checkboxValue}
          onChange={() => { impact ? this.props.onImpactExclusionsChange(projectExclusionType) : this.props.onNetworkExclusionsChange(projectExclusionType); }}
          disabled={disabled}
          testId={"edit-display-options__" + checkboxText + "-checkbox"}
          indeterminate={checkboxValue === undefined}
          checkboxRef={checkboxRef}
        />
      </div>
    );
  }

  private renderCheckbox(
    checkboxKey: keyof Project,
    checkboxText: string,
    eventProperty: UpdateResourceKeys,
    checkboxCssClass: string,
    outerDivCssClass: string,
    disabled: boolean,
    checkboxValue?: boolean,
    tipText?: string,
    isDependent?: boolean,
    checkboxRef?: RefObject<HTMLInputElement>,
  ) {
    if (this.props.shared && !checkboxValue) {
      //only display checked checkboxes for shared users
      return null;
    }
    const inputId = uniqueId();
    return (
      <div className={outerDivCssClass} key={checkboxText}>
        <TooltipWrapper text={tipText} containerId={this.props.sidePanelId}>
          <QESCheckbox
            containerClassName={`${checkboxCssClass} form-check mt-0${isDependent ? " ms-4" : ""}`}
            label={
              <span style={{ color: checkboxValue == undefined ? "red" : "inherit" }}>{checkboxText}</span>
            }
            id={inputId}
            className="custom-control-inline"
            checked={checkboxValue}
            onChange={() => { this.props.onCheckBoxChange(eventProperty); }}
            disabled={disabled}
            testId={"edit-display-options__" + checkboxKey + "-checkbox"}
            indeterminate={checkboxValue === undefined}
            checkboxRef={checkboxRef}
          />
        </TooltipWrapper>
      </div>
    );
  }
}

const EditDisplayOptions = withFeatureFlagContext(WrappedEditDisplayOptions);
export { EditDisplayOptions };
